import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Table } from "../../components/ui/Table";
import { Modal } from "../../components/ui/Modal";

interface AseguradoraRow {
  id: number;
  nombre: string;
  emailContacto?: string;
  email_contacto?: string;
  email?: string;
  createdAt?: string;
  created_at?: string;
}

export function AseguradorasPage() {
  const [data, setData] = useState<AseguradoraRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState<"create" | "edit" | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({ nombre: "", email_contacto: "", email: "", password: "" });
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<AseguradoraRow[]>("/admin/aseguradoras");
      setData(Array.isArray(res.data) ? res.data : []);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openCreate = () => {
    setForm({ nombre: "", email_contacto: "", email: "", password: "" });
    setFormError("");
    setEditId(null);
    setModalOpen("create");
  };

  const openEdit = (row: AseguradoraRow) => {
    setEditId(row.id);
    setForm({
      nombre: row.nombre,
      email_contacto: (row.emailContacto ?? row.email_contacto) ?? "",
      email: row.email ?? "",
      password: "",
    });
    setFormError("");
    setModalOpen("edit");
  };

  const handleCreateSubmit = async () => {
    if (!form.nombre?.trim() || !form.email_contacto?.trim() || !form.email?.trim() || !form.password) {
      setFormError("Completa todos los campos");
      return;
    }
    if (form.password.length < 8) {
      setFormError("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      await api.post("/admin/aseguradoras", {
        nombre: form.nombre.trim(),
        email_contacto: form.email_contacto.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      setModalOpen(null);
      fetchList();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setFormError(err.response?.data?.error ?? "Error al crear");
    } finally {
      setSaving(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!editId) return;
    if (!form.nombre?.trim() || !form.email_contacto?.trim()) {
      setFormError("Nombre y email de contacto son obligatorios");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      await api.patch(`/admin/aseguradoras/${editId}`, {
        nombre: form.nombre.trim(),
        email_contacto: form.email_contacto.trim(),
      });
      setModalOpen(null);
      fetchList();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setFormError(err.response?.data?.error ?? "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d: string | undefined) => {
    if (!d) return "-";
    try {
      return new Date(d).toLocaleDateString("es-ES");
    } catch {
      return d;
    }
  };

  const columns = [
    { key: "nombre", header: "Nombre", render: (r: AseguradoraRow) => r.nombre },
    { key: "email_contacto", header: "Email contacto", render: (r: AseguradoraRow) => r.emailContacto ?? r.email_contacto ?? "-" },
    { key: "email", header: "Email acceso", render: (r: AseguradoraRow) => r.email ?? "-" },
    { key: "createdAt", header: "Fecha alta", render: (r: AseguradoraRow) => formatDate(r.createdAt ?? r.created_at) },
    {
      key: "actions",
      header: "Acciones",
      render: (r: AseguradoraRow) => (
        <Button size="sm" variant="secondary" onClick={() => openEdit(r)}>Editar</Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-[#2C2C2C]">Aseguradoras</h1>
        <Button onClick={openCreate}>Nueva aseguradora</Button>
      </div>

      <Table<AseguradoraRow> columns={columns} data={data} loading={loading} emptyMessage="No hay aseguradoras" />

      <Modal isOpen={modalOpen === "create"} onClose={() => setModalOpen(null)} title="Nueva aseguradora" footer={
        <>
          <Button variant="secondary" onClick={() => setModalOpen(null)}>Cancelar</Button>
          <Button loading={saving} onClick={handleCreateSubmit}>Crear</Button>
        </>
      }>
        {formError && <p className="mb-4 text-sm text-red-600">{formError}</p>}
        <div className="space-y-4">
          <Input label="Nombre" value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} required />
          <Input label="Email de contacto" type="email" value={form.email_contacto} onChange={(e) => setForm((f) => ({ ...f, email_contacto: e.target.value }))} required />
          <Input label="Email (acceso)" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required />
          <Input label="Contraseña" type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} required />
        </div>
      </Modal>

      <Modal isOpen={modalOpen === "edit"} onClose={() => setModalOpen(null)} title="Editar aseguradora" footer={
        <>
          <Button variant="secondary" onClick={() => setModalOpen(null)}>Cancelar</Button>
          <Button loading={saving} onClick={handleEditSubmit}>Guardar</Button>
        </>
      }>
        {formError && <p className="mb-4 text-sm text-red-600">{formError}</p>}
        <div className="space-y-4">
          <Input label="Nombre" value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} required />
          <Input label="Email de contacto" type="email" value={form.email_contacto} onChange={(e) => setForm((f) => ({ ...f, email_contacto: e.target.value }))} required />
          <p className="text-sm text-gray-500">El email de acceso no se puede cambiar desde aquí.</p>
        </div>
      </Modal>
    </div>
  );
}
